import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import { openPrintWindow } from '@/utils/printWindow';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, Plus, Sparkles, Smile, Meh, Frown, Sun, Moon, Utensils, Building2, Calendar, Download, Printer, Search, ChevronRight
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import API from '@/config/api';
// API imported from config;

const MobileReportRow = ({ report, moodIcon, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="w-full flex items-center gap-3 p-3 rounded-2xl border bg-card text-left active:scale-[0.99] transition-transform"
    data-testid={`mobile-report-row-${report.id}`}
  >
    <div className="w-11 h-11 rounded-full bg-primary/10 text-primary font-semibold flex items-center justify-center flex-shrink-0">
      {(report.child_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold truncate">{report.child_name}</p>
      <p className="text-xs text-muted-foreground truncate">
        {report.date} · {report.activities.length} {report.activities.length === 1 ? 'activity' : 'activities'}
        {report.ai_summary ? ' · AI summary' : ''}
      </p>
    </div>
    <div className="flex-shrink-0 [&_svg]:w-6 [&_svg]:h-6">{moodIcon}</div>
    <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
  </button>
);

const DailyReportsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [reports, setReports] = useState([]);
  const [children, setChildren] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generatingAI, setGeneratingAI] = useState(null);
  const [filterClassroom, setFilterClassroom] = useState('all');
  const [filterDate, setFilterDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [previewSummary, setPreviewSummary] = useState('');
  const [generatingPreview, setGeneratingPreview] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Helper to normalize time values to HH:MM format for type="time" inputs
  const normalizeTime = (val) => {
    if (!val) return '';
    // Already in HH:MM format
    if (/^\d{2}:\d{2}$/.test(val)) return val;
    // H:MM format
    if (/^\d{1}:\d{2}$/.test(val)) return '0' + val;
    // Handle 12-hour format (e.g. "1:30 PM")
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM|am|pm)?$/);
    if (match) {
      let h = parseInt(match[1]);
      const m = match[2];
      const period = (match[3] || '').toUpperCase();
      if (period === 'PM' && h < 12) h += 12;
      if (period === 'AM' && h === 12) h = 0;
      return `${String(h).padStart(2, '0')}:${m}`;
    }
    return val;
  };

  const printRef = useRef(null);
  const [formData, setFormData] = useState({
    child_id: '',
    date: new Date().toISOString().split('T')[0],
    activities: '',
    meals: { breakfast: '', lunch: '', snack: '', dinner: '' },
    nap_times: [{ start: '', end: '' }],
    mood: 'happy',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reportsRes, childrenRes, classroomsRes] = await Promise.all([
        axios.get(`${API}/daily-reports`, getAuthHeaders()),
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/classrooms`, getAuthHeaders())
      ]);
      setReports(reportsRes.data);
      setChildren(childrenRes.data);
      setClassrooms(classroomsRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReport = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      activities: formData.activities.split(',').map(a => a.trim()).filter(Boolean),
      nap_times: formData.nap_times.filter(n => n.start && n.end),
      ai_summary: previewSummary || undefined
    };

    try {
      await axios.post(`${API}/daily-reports`, payload, getAuthHeaders());
      toast.success('Daily report created');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create report');
    }
  };

  const generatePreviewSummary = async () => {
    if (!formData.child_id) {
      toast.error('Please select a child first');
      return;
    }
    
    const selectedChild = children.find(c => c.id === formData.child_id);
    if (!selectedChild) {
      toast.error('Child not found');
      return;
    }
    
    setGeneratingPreview(true);
    try {
      const previewData = {
        child_name: `${selectedChild.first_name} ${selectedChild.last_name}`,
        date: formData.date,
        activities: formData.activities.split(',').map(a => a.trim()).filter(Boolean),
        meals: formData.meals,
        nap_times: formData.nap_times.filter(n => n.start && n.end),
        mood: formData.mood,
        notes: formData.notes
      };
      
      const response = await axios.post(
        `${API}/daily-reports/preview-summary`,
        previewData,
        getAuthHeaders()
      );
      
      setPreviewSummary(response.data.summary);
      toast.success('AI summary generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview summary');
    } finally {
      setGeneratingPreview(false);
    }
  };

  const downloadReportPdf = async (reportId) => {
    setDownloadingPdf(reportId);
    try {
      const response = await axios.get(
        `${API}/daily-reports/${reportId}/pdf`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily_report_${reportId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const printReport = () => {
    if (!selectedReport) return;
    
    // Create a printable version of the report
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Daily Report - ${selectedReport.child_name}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
          h1 { color: #4F46E5; text-align: center; }
          h2 { color: #6B7280; font-size: 14px; text-align: center; }
          .section { margin: 20px 0; }
          .section-title { font-weight: bold; color: #4F46E5; border-bottom: 1px solid #E5E7EB; padding-bottom: 5px; margin-bottom: 10px; }
          .badge { display: inline-block; background: #F3F4F6; padding: 4px 8px; border-radius: 4px; margin: 2px; font-size: 12px; }
          .summary-box { background: #F5F3FF; border: 1px solid #4F46E5; border-radius: 8px; padding: 15px; margin-top: 20px; }
          .info-row { display: flex; gap: 30px; margin-bottom: 10px; }
          .info-item { }
          .info-label { font-size: 12px; color: #6B7280; }
          .info-value { font-size: 14px; font-weight: 500; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>CareKids WI</h1>
        <h2>Daily Activity Report</h2>
        
        <div class="info-row">
          <div class="info-item">
            <div class="info-label">Child's Name</div>
            <div class="info-value">${selectedReport.child_name}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Date</div>
            <div class="info-value">${selectedReport.date}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Mood</div>
            <div class="info-value">${selectedReport.mood === 'happy' ? '😊 Happy' : selectedReport.mood === 'okay' ? '😐 Okay' : '😟 Fussy'}</div>
          </div>
        </div>
        
        <div class="section">
          <div class="section-title">Today's Activities</div>
          ${selectedReport.activities.map(a => `<span class="badge">${a}</span>`).join(' ')}
        </div>
        
        <div class="section">
          <div class="section-title">Meals</div>
          ${Object.entries(selectedReport.meals || {}).filter(([_, v]) => v).map(([meal, food]) => `<div><strong>${meal}:</strong> ${food}</div>`).join('')}
        </div>
        
        ${selectedReport.nap_times && selectedReport.nap_times[0]?.start ? `
        <div class="section">
          <div class="section-title">Nap Time</div>
          <div>${selectedReport.nap_times[0].start} - ${selectedReport.nap_times[0].end || 'N/A'}</div>
        </div>
        ` : ''}
        
        ${selectedReport.notes ? `
        <div class="section">
          <div class="section-title">Teacher's Notes</div>
          <div>${selectedReport.notes}</div>
        </div>
        ` : ''}
        
        ${selectedReport.ai_summary ? `
        <div class="summary-box">
          <div class="section-title">✨ AI Summary</div>
          <div>${selectedReport.ai_summary}</div>
        </div>
        ` : ''}
      </body>
      </html>
    `;
    
    const printWindow = openPrintWindow(printContent);
    if (!printWindow) {
      toast?.error?.('Please allow pop-ups to print');
    }
  };

  const generateAISummary = async (reportId) => {
    setGeneratingAI(reportId);
    try {
      const response = await axios.post(
        `${API}/daily-reports/${reportId}/generate-summary`,
        {},
        getAuthHeaders()
      );
      toast.success('AI summary generated!');
      fetchData();
      // Update selected report if open
      if (selectedReport?.id === reportId) {
        setSelectedReport({...selectedReport, ai_summary: response.data.summary});
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI summary');
    } finally {
      setGeneratingAI(null);
    }
  };

  const resetForm = () => {
    setFormData({
      child_id: '',
      date: new Date().toISOString().split('T')[0],
      activities: '',
      meals: { breakfast: '', lunch: '', snack: '', dinner: '' },
      nap_times: [{ start: '', end: '' }],
      mood: 'happy',
      notes: ''
    });
    setPreviewSummary('');
  };

  const getMoodIcon = (mood) => {
    const icons = {
      happy: <Smile className="w-5 h-5 text-green-500" />,
      okay: <Meh className="w-5 h-5 text-amber-500" />,
      fussy: <Frown className="w-5 h-5 text-red-500" />
    };
    return icons[mood] || icons.happy;
  };

  if (loading) {
    return (
      <DashboardLayout title="Daily Reports">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  const canCreate = user.role === 'admin' || user.role === 'provider';

  // Get classroom for a child
  const getChildClassroom = (childId) => {
    const child = children.find(c => c.id === childId);
    return child?.classroom || 'Unassigned';
  };

  // Filter reports
  const filteredReports = reports.filter(r => {
    const childClassroom = getChildClassroom(r.child_id);
    if (filterClassroom !== 'all' && childClassroom !== filterClassroom) return false;
    if (filterDate && r.date !== filterDate) return false;
    if (searchQuery.trim() && !(r.child_name || '').toLowerCase().includes(searchQuery.trim().toLowerCase())) return false;
    return true;
  });

  // Group reports by classroom
  const reportsByClassroom = filteredReports.reduce((acc, report) => {
    const classroom = getChildClassroom(report.child_id);
    if (!acc[classroom]) acc[classroom] = [];
    acc[classroom].push(report);
    return acc;
  }, {});

  return (
    <DashboardLayout title="Daily Reports">
      <div className="space-y-6" data-testid="daily-reports-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-muted-foreground">
            {user.role === 'parent' 
              ? 'View daily activity reports for your children' 
              : 'Create and manage daily activity reports'}
          </p>
          {canCreate && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="btn-primary w-full sm:w-auto" data-testid="create-report-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Report
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full p-4 sm:p-6">
                <DialogHeader>
                  <DialogTitle className="font-heading">Create Daily Report</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateReport} className="space-y-4 mt-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Child</Label>
                      <Select 
                        value={formData.child_id} 
                        onValueChange={(value) => setFormData({...formData, child_id: value})}
                      >
                        <SelectTrigger data-testid="report-child-select">
                          <SelectValue placeholder="Select child" />
                        </SelectTrigger>
                        <SelectContent>
                          {children.map((child) => (
                            <SelectItem key={child.id} value={child.id}>
                              {child.first_name} {child.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({...formData, date: e.target.value})}
                        required
                        data-testid="report-date-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Activities (comma separated)</Label>
                    <Input
                      value={formData.activities}
                      onChange={(e) => setFormData({...formData, activities: e.target.value})}
                      placeholder="e.g., Art project, Outdoor play, Story time, Singing"
                      required
                      data-testid="report-activities-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Meals</Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <Input
                        placeholder="Breakfast"
                        value={formData.meals.breakfast}
                        onChange={(e) => setFormData({...formData, meals: {...formData.meals, breakfast: e.target.value}})}
                        data-testid="report-breakfast-input"
                      />
                      <Input
                        placeholder="Lunch"
                        value={formData.meals.lunch}
                        onChange={(e) => setFormData({...formData, meals: {...formData.meals, lunch: e.target.value}})}
                        data-testid="report-lunch-input"
                      />
                      <Input
                        placeholder="Snack"
                        value={formData.meals.snack}
                        onChange={(e) => setFormData({...formData, meals: {...formData.meals, snack: e.target.value}})}
                        data-testid="report-snack-input"
                      />
                      <Input
                        placeholder="Dinner"
                        value={formData.meals.dinner || ''}
                        onChange={(e) => setFormData({...formData, meals: {...formData.meals, dinner: e.target.value}})}
                        data-testid="report-dinner-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Nap Time</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        type="time"
                        placeholder="Start"
                        value={normalizeTime(formData.nap_times[0]?.start)}
                        onChange={(e) => setFormData({
                          ...formData, 
                          nap_times: [{...formData.nap_times[0], start: e.target.value}]
                        })}
                        data-testid="report-nap-start-input"
                      />
                      <Input
                        type="time"
                        placeholder="End"
                        value={normalizeTime(formData.nap_times[0]?.end)}
                        onChange={(e) => setFormData({
                          ...formData, 
                          nap_times: [{...formData.nap_times[0], end: e.target.value}]
                        })}
                        data-testid="report-nap-end-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Overall Mood</Label>
                    <Select 
                      value={formData.mood} 
                      onValueChange={(value) => setFormData({...formData, mood: value})}
                    >
                      <SelectTrigger data-testid="report-mood-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="happy">Happy 😊</SelectItem>
                        <SelectItem value="okay">Okay 😐</SelectItem>
                        <SelectItem value="fussy">Fussy 😟</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Teacher's Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                      placeholder="Any additional observations about the day..."
                      rows={3}
                      data-testid="report-notes-input"
                    />
                  </div>

                  {/* AI Summary Preview Section */}
                  <div className="border-t pt-4 mt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        AI Summary Preview
                      </h4>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={generatePreviewSummary}
                        disabled={generatingPreview || !formData.child_id || !formData.activities}
                        data-testid="preview-summary-btn"
                      >
                        {generatingPreview ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current mr-2"></div>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Preview
                          </>
                        )}
                      </Button>
                    </div>
                    {previewSummary ? (
                      <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                        <p className="text-sm whitespace-pre-wrap">{previewSummary}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          This summary will be included with your report.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Fill out the form above and click "Generate Preview" to see an AI-generated summary before creating the report.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="cancel-report-btn">
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="save-report-btn">
                      Create Report
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Report Detail Dialog */}
        <Dialog open={!!selectedReport} onOpenChange={(open) => !open && setSelectedReport(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100vw-1.5rem)] sm:w-full p-4 sm:p-6" data-testid="report-detail-dialog">
            {selectedReport && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Daily Report - {selectedReport.child_name}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-6 mt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{selectedReport.date}</span>
                    <div className="flex items-center gap-2">
                      {getMoodIcon(selectedReport.mood)}
                      <span className="capitalize">{selectedReport.mood}</span>
                    </div>
                  </div>

                  {/* Activities */}
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <Sun className="w-4 h-4 text-primary" />
                      Activities
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedReport.activities.map((activity, idx) => (
                        <Badge key={idx} variant="secondary">{activity}</Badge>
                      ))}
                    </div>
                  </div>

                  {/* Meals */}
                  {Object.keys(selectedReport.meals || {}).length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Utensils className="w-4 h-4 text-primary" />
                        Meals
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {Object.entries(selectedReport.meals).map(([meal, food]) => (
                          food && (
                            <div key={meal} className="p-3 bg-muted rounded-xl">
                              <p className="text-xs text-muted-foreground capitalize">{meal}</p>
                              <p className="font-medium">{food}</p>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Nap Times */}
                  {selectedReport.nap_times && selectedReport.nap_times.length > 0 && selectedReport.nap_times[0].start && (
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <Moon className="w-4 h-4 text-primary" />
                        Nap Time
                      </h4>
                      <p>{normalizeTime(selectedReport.nap_times[0].start)} - {normalizeTime(selectedReport.nap_times[0].end)}</p>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedReport.notes && (
                    <div>
                      <h4 className="font-semibold mb-2">Teacher's Notes</h4>
                      <p className="text-muted-foreground">{selectedReport.notes}</p>
                    </div>
                  )}

                  {/* AI Summary */}
                  <div className="border-t pt-4">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-primary" />
                        AI Summary
                      </h4>
                      {canCreate && !selectedReport.ai_summary && (
                        <Button
                          size="sm"
                          onClick={() => generateAISummary(selectedReport.id)}
                          disabled={generatingAI === selectedReport.id}
                          data-testid="generate-ai-summary-btn"
                        >
                          {generatingAI === selectedReport.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current mr-2"></div>
                              Generating...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-4 h-4 mr-2" />
                              Generate Summary
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    {selectedReport.ai_summary ? (
                      <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                        <p className="whitespace-pre-wrap">{selectedReport.ai_summary}</p>
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">
                        No AI summary generated yet. Click the button to create one.
                      </p>
                    )}
                  </div>

                  {/* Print/Download Actions */}
                  <div className="border-t pt-4 grid grid-cols-2 sm:flex sm:justify-end gap-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={printReport}
                      data-testid="print-report-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => downloadReportPdf(selectedReport.id)}
                      disabled={downloadingPdf === selectedReport.id}
                      data-testid="download-report-btn"
                    >
                      {downloadingPdf === selectedReport.id ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-current mr-2"></div>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Classroom Filters */}
        {user.role !== 'parent' && (
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
                <div className="w-full sm:flex-1 sm:min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Search Child</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by child name..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                      data-testid="report-search-input"
                    />
                  </div>
                </div>
                <div className="w-full sm:flex-1 sm:min-w-[200px]">
                  <Label className="text-xs text-muted-foreground">Filter by Classroom</Label>
                  <Select value={filterClassroom} onValueChange={setFilterClassroom}>
                    <SelectTrigger data-testid="report-classroom-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Classrooms</SelectItem>
                      {classrooms.map(c => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-full sm:w-[180px]">
                  <Label className="text-xs text-muted-foreground">Filter by Date</Label>
                  <Input 
                    type="date" 
                    value={filterDate} 
                    onChange={e => setFilterDate(e.target.value)}
                    data-testid="report-date-filter"
                  />
                </div>
                {(filterClassroom !== 'all' || filterDate || searchQuery) && (
                  <div className="flex items-end">
                    <Button variant="ghost" size="sm" className="w-full sm:w-auto" onClick={() => { setFilterClassroom('all'); setFilterDate(''); setSearchQuery(''); }} data-testid="clear-report-filters-btn">
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reports Grid - Grouped by Classroom */}
        {filteredReports.length > 0 ? (
          <div className="space-y-6">
            {Object.entries(reportsByClassroom).map(([classroom, classroomReports]) => (
              <div key={classroom}>
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-4 h-4 text-primary" />
                  <h3 className="font-semibold">{classroom}</h3>
                  <Badge variant="secondary">{classroomReports.length} reports</Badge>
                </div>
                <div className="md:hidden space-y-2" data-testid={`mobile-report-list-${classroom}`}>
                  {classroomReports.map((report) => (
                    <MobileReportRow key={report.id} report={report} moodIcon={getMoodIcon(report.mood)} onOpen={() => setSelectedReport(report)} />
                  ))}
                </div>
                <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {classroomReports.map((report) => (
                    <Card 
                      key={report.id} 
                      className="card-interactive"
                      onClick={() => setSelectedReport(report)}
                      data-testid={`report-card-${report.id}`}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="font-semibold text-lg">{report.child_name}</h3>
                            <p className="text-sm text-muted-foreground">{report.date}</p>
                          </div>
                          {getMoodIcon(report.mood)}
                        </div>

                        <div className="flex flex-wrap gap-1 mb-4">
                          {report.activities.slice(0, 3).map((activity, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {activity}
                            </Badge>
                          ))}
                          {report.activities.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{report.activities.length - 3} more
                            </Badge>
                          )}
                        </div>

                        {report.ai_summary && (
                          <div className="flex items-center gap-1 text-xs text-primary">
                            <Sparkles className="w-3 h-3" />
                            AI Summary Available
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="card-clay">
            <CardContent className="p-12 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading font-bold text-xl mb-2">No Daily Reports</h3>
              <p className="text-muted-foreground mb-6">
                {canCreate 
                  ? 'Create your first daily report to share with parents.' 
                  : 'No reports available yet.'}
              </p>
              {canCreate && (
                <Button onClick={() => setDialogOpen(true)} data-testid="empty-create-report-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Report
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DailyReportsPage;
